import React from 'react';
import {
  List,
  BulkDeleteWithConfirmButton,
  Datagrid,
  TextField,
  Filter,
  TextInput,
  SelectInput,
  useNotify,
  useRefresh
} from 'react-admin';
import withStyles from '@material-ui/core/styles/withStyles';
import {
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
  Chip,
  Box
} from '@material-ui/core';
import ImageIcon from '@material-ui/icons/Image';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import CancelIcon from '@material-ui/icons/Cancel';
import { HTTP_POST } from '../../common/HttpService';
import { apiUrl } from '../../common/Globals';

const ToolsFilter = (props) => (
  <Filter {...props}>
    {/* <TextInput label="Search" source="q" alwaysOn /> */}
    <TextInput source="vendorId" label="Vendor Id" alwaysOn />
    <TextInput source="equipmentType" label="Equipment Type" alwaysOn />
    <TextInput source="brand" label="Brand" alwaysOn />
    <TextInput source="rent" label="Rental Charge" alwaysOn />
    <SelectInput
      source="currentStatus"
      label="Status"
      alwaysOn
      choices={[
        { id: 'approved', name: 'Approved' },
        { id: 'pending approval', name: 'Pending Approval' },
        { id: 'disqualified', name: 'Disqualified' }
      ]}
    />
    <TextInput source="description" label="Description" />
  </Filter>
);

const rowClick = () => {
  return 'edit';
};

const ToolsBulkActionButtons = (props) => <BulkDeleteWithConfirmButton {...props} />;

const styles = {
  image: {
    maxHeight: '50px',
    maxWidth: '50px',
    borderRadius: '30%'
  }
};

const ApprovalStatusField = ({record}) => {
  const [loading, setLoading] = React.useState(false);
  const notify = useNotify();
  const refresh = useRefresh();

  if (!record) return null;
  const rawStatus = (record.currentStatus || record.status || '').toLowerCase().trim();
  const isApproved =
    rawStatus === 'approved' ||
    record.isAdminApproved === true ||
    record.isAdminApproved === 'true';
  const isDisqualified =
    rawStatus === 'disqualified' ||
    rawStatus === 'rejected';

  const handleQuickApprove = async (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    try {
      setLoading(true);
      await HTTP_POST(`${apiUrl}/tool/update/${record.id}`, {
        ...record,
        currentStatus: 'approved'
      });
      notify('Tool & Equipment approved successfully', 'info');
      refresh();
    } catch (err) {
      notify('Failed to approve: ' + (err.message || 'Error'), 'warning');
    } finally {
      setLoading(false);
    }
  };

  if (isApproved) {
    return (
      <Chip
        icon={<CheckCircleIcon style={{ color: '#fff', fontSize: 14 }} />}
        label="Approved"
        style={{
          backgroundColor: '#059669',
          color: '#ffffff',
          fontWeight: 700,
          fontSize: '0.72rem',
          height: 24,
          paddingLeft: 4
        }}
        size="small"
      />
    );
  }

  if (isDisqualified) {
    return (
      <Chip
        icon={<CancelIcon style={{ color: '#fff', fontSize: 14 }} />}
        label="Disqualified"
        style={{
          backgroundColor: '#DC2626',
          color: '#ffffff',
          fontWeight: 700,
          fontSize: '0.72rem',
          height: 24,
          paddingLeft: 4
        }}
        size="small"
      />
    );
  }

  return (
    <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <Chip
        icon={<HourglassEmptyIcon style={{ color: '#fff', fontSize: 14 }} />}
        label="Pending"
        style={{
          backgroundColor: '#D97706',
          color: '#ffffff',
          fontWeight: 700,
          fontSize: '0.72rem',
          height: 24,
          paddingLeft: 4
        }}
        size="small"
      />
      <Button
        size="small"
        variant="contained"
        disabled={loading}
        onClick={handleQuickApprove}
        style={{
          fontSize: '0.65rem',
          padding: '2px 8px',
          minWidth: 'auto',
          textTransform: 'none',
          backgroundColor: '#059669',
          color: '#ffffff',
          fontWeight: 700,
          height: 22,
          borderRadius: 4
        }}
      >
        {loading ? '...' : 'Approve'}
      </Button>
    </Box>
  );
};

const PhotoField = ({record, source = 'toolImagePath'}) => {
  const [open, setOpen] = React.useState(false);

  if (!record || !record[source]) return <span>-</span>;

  const handleOpen = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setOpen(true);
  };

  const handleClose = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setOpen(false);
  };

  return (
    <>
      <Tooltip title="View Photo">
        <IconButton color="primary" onClick={handleOpen} size="small">
          <ImageIcon />
        </IconButton>
      </Tooltip>
      <Dialog open={open} onClose={handleClose} maxWidth="md" onClick={(e) => e.stopPropagation()}>
        <DialogTitle style={{backgroundColor: '#1A3254', color: '#fff'}}>
          Photo - {record.equipmentType || ''}
        </DialogTitle>
        <DialogContent style={{padding: '20px', textAlign: 'center'}}>
          <img
            src={record[source]}
            alt={record.equipmentType || 'Photo'}
            style={{maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: '4px'}}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary" variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const ToolsList = ({classes, ...props}) => (
  <List
    title="Tool & Equipments"
    hasCreate={false}
    {...props}
    filters={<ToolsFilter />}
    bulkActionButtons={<ToolsBulkActionButtons />}
  >
    <Datagrid rowClick={rowClick}>
      <TextField source="vendorId" label="Vendor Id" />
      <TextField source="equipmentType" label="Equipment Type" />
      <TextField source="equipmentSpecification" label="Equipment Spec" />
      <TextField source="brand" label="Brand" />
      <TextField source="rent" label="Rental Charge" />
      <TextField source="estimationType" label="Estimation Type" />
      <TextField source="description" label="Description" />
      <ApprovalStatusField label="Status" />
      <PhotoField source="toolImagePath" label="Photo" />
    </Datagrid>
  </List>
);

export default withStyles(styles)(ToolsList);

